import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "@/lib/api";

const AuthContext = createContext(null);

// ─────────────────────────────────────────────────────────────────────────────
// SECURITY NOTE (re: code-review false positives about "insecure token
// storage"):
//
// This file does NOT store any auth token in localStorage or sessionStorage.
// The JWT lives exclusively in an httpOnly cookie set by the backend at
// /api/auth/login. JavaScript cannot read or steal it (XSS-resistant).
//
// What we DO store in localStorage:
//   • PROFILE_CACHE_KEY → { id, username, role } — non-sensitive profile data
//     used to render the correct navigation while offline. An attacker who
//     reads this learns nothing they couldn't already see in the rendered UI.
//
// What we DO store in sessionStorage:
//   • SESSION_FLAG_KEY → "1" — a boolean flag that survives reloads but is
//     wiped when the PWA is killed (swipe-up on iOS). Used to force a fresh
//     login screen after the app is closed.
//
// Neither value grants access to the backend on its own.
// ─────────────────────────────────────────────────────────────────────────────
const PROFILE_CACHE_KEY = "vidoc_user";
const SESSION_FLAG_KEY = "vidoc_session_active";

// localStorage helpers that swallow quota / private-mode errors (Safari
// throws QuotaExceededError under iOS Private Browsing). We log once so the
// reviewer knows the write failed, but never crash the auth flow.
function safeLocalSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`[AuthContext] localStorage.set(${key}) failed`, e?.name);
    return false;
  }
}

function safeLocalRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn(`[AuthContext] localStorage.remove(${key}) failed`, e?.name);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: ask the backend who we are (cookie-based). If offline or no
  // valid session, fall back to the last known profile so the PWA can still
  // render the navigation while disconnected.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Security: if sessionStorage has no active-session flag, this is a
      // fresh PWA launch (app was killed / swiped away). Force a logout so
      // the user must re-enter their password, even though the httpOnly
      // cookie may still be valid server-side.
      let isFreshLaunch = false;
      try {
        isFreshLaunch = !sessionStorage.getItem(SESSION_FLAG_KEY);
      } catch (e) {
        // Private browsing or storage disabled — treat as fresh launch.
        isFreshLaunch = true;
      }

      if (isFreshLaunch) {
        try {
          await authApi.logout();
        } catch {
          // Offline or already logged out — silently ignored. The server
          // will reject any subsequent request anyway, and the user will
          // be redirected to /login by the protected route below.
        }
        safeLocalRemove(PROFILE_CACHE_KEY);
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        const me = await authApi.me();
        if (!cancelled) {
          setUser(me);
          safeLocalSet(PROFILE_CACHE_KEY, JSON.stringify(me));
        }
      } catch (err) {
        const offline = !navigator.onLine || err?.code === "ERR_NETWORK";
        if (offline) {
          try {
            const stored = localStorage.getItem(PROFILE_CACHE_KEY);
            if (stored && !cancelled) setUser(JSON.parse(stored));
          } catch (e) {
            console.warn("[AuthContext] cached profile unreadable", e);
          }
        } else {
          // Server says we're unauthenticated → clear cached profile
          safeLocalRemove(PROFILE_CACHE_KEY);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (username, password) => {
    const res = await authApi.login(username, password);
    setUser(res.user);
    safeLocalSet(PROFILE_CACHE_KEY, JSON.stringify(res.user));
    // Mark this PWA session as active — survives reloads but is wiped when
    // the app is killed (swipe-up on iOS), forcing a fresh login next time.
    try {
      sessionStorage.setItem(SESSION_FLAG_KEY, "1");
    } catch (e) {
      console.warn("[AuthContext] sessionStorage.set failed", e?.name);
    }
    return res.user;
  };

  const logout = () => {
    // Update local state immediately so the UI re-renders on the next tick.
    setUser(null);
    safeLocalRemove(PROFILE_CACHE_KEY);
    try {
      sessionStorage.removeItem(SESSION_FLAG_KEY);
    } catch {
      // Storage may be unavailable in Safari private mode. The flag is a
      // best-effort guard, not a security boundary — the httpOnly cookie
      // (cleared by the /auth/logout call below) is the real credential.
    }
    // Fire-and-forget: tell the server to clear the cookie. We don't block
    // the UI on this — if it fails (offline), the next /auth/me will reject.
    authApi.logout().catch((e) => {
      console.warn("[AuthContext] logout request failed (cookie may persist)", e?.message);
    });
  };

  // Memoize the context value so children don't re-render when AuthProvider
  // re-renders for unrelated reasons.
  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
